import { useLanguage } from '../i18n/LanguageContext';
import { useCallback, useState, useEffect } from 'react';
import { fetchDevices } from '../api/devices';
import Pagination from '../components/Pagination';
import { can } from '../utils/permissions';
import './DevicesPage.css';

const PAGE_SIZE = 50;

function DevicesPage() {
  const { t } = useLanguage();
  const canReadDevices = can('device.read');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const loadDevices = useCallback(async () => {
    if (!canReadDevices) {
      setLoading(false);
      setError('기기 조회 권한이 없습니다.');
      setDevices([]);
      setTotal(0);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const offset = (currentPage - 1) * PAGE_SIZE;
      const result = await fetchDevices({
        offset,
        limit: PAGE_SIZE,
        search: searchTerm.trim(),
      });
      setDevices(result.data.devices ?? []);
      setTotal(Number(result.data.total ?? 0));
    } catch (err) {
      console.error('failed to fetch devices:', err);
      setError('기기 목록을 불러오지 못했습니다.');
      setDevices([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canReadDevices, currentPage, searchTerm]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const filteredDevices = devices;

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (!canReadDevices) return;
    setSearchTerm(searchInput.trim());
    setCurrentPage(1);
  };

  const formatDate = (dateString, withTime = false) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    const datePart = `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}`;
    if (withTime) {
      return `${datePart} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
    return datePart;
  };

  const formatDeviceModel = (device) => (
    device.model ?? '-'
  );

  const formatOwner = (ownerType, ownerUuid) => {
    if (!ownerType && !ownerUuid) return '-';
    if (!ownerUuid) return ownerType;
    if (!ownerType) return ownerUuid;
    return `${ownerType} (${ownerUuid})`;
  };

  const formatWalletUuids = (walletUuids) => {
    if (!Array.isArray(walletUuids) || walletUuids.length === 0) return '-';
    return walletUuids.join(', ');
  };

  const getPaginationInfo = () => {
    if (searchTerm) {
      return `현재 페이지 검색 결과 ${filteredDevices.length.toLocaleString()}대 / 전체 ${Number(total).toLocaleString()}대`;
    }

    if (total === 0) return '총 0대 표시';

    const start = (currentPage - 1) * PAGE_SIZE + 1;
    const end = Math.min(start + devices.length - 1, total);
    return `총 ${Number(total).toLocaleString()}대 중 ${start.toLocaleString()}-${end.toLocaleString()} 표시`;
  };

  return (
    <div className="devices-page">
      {/* 헤더 */}
      <div className="page-header">
        <div className="page-title-group">
          <div>
            <h2 className="page-title">{t("기기 관리")}</h2>
            <p className="page-subtitle">{t("등록된 기기의 상태를 확인하고 관리합니다.")}</p>
          </div>
          <div className="devices-summary-bar">
            <span>{t("전체 조회된 기기 수")}</span>
            <strong>{Number(total).toLocaleString()}</strong>
          </div>
        </div>
        <div className="header-actions">
          <form className="search-form" onSubmit={handleSearchSubmit}>
            <div className="search-box">
              <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder={t("시리얼넘버, 사용자 아이디 입력 후 Enter")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                disabled={!canReadDevices}
              />
            </div>
            <button className="btn-search" type="submit" disabled={loading || !canReadDevices}>
              {t("검색")}
            </button>
          </form>
        </div>
      </div>

      {/* 테이블 */}
      <div className="table-card">
        <table className="devices-table">
          <thead>
            <tr>
              <th>{t("모델")}</th>
              <th>{t("시리얼 번호")}</th>
              <th>{t("최초 소유자")}</th>
              <th>{t("현재 소유자")}</th>
              <th>{t("등록일")}</th>
              <th>{t("업데이트 날짜")}</th>
              <th>{t("버전")}</th>
              <th>{t("업그레이드")}</th>
              <th style={{ textAlign: 'center' }}>{t("관리")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>{t("데이터를 불러오는 중입니다...")}</td></tr>
            ) : error ? (
              <tr><td colSpan="9" className="table-error">{error}</td></tr>
            ) : filteredDevices.length > 0 ? (
              filteredDevices.map(device => (
                <tr
                  key={device.uuid}
                  className={`device-row ${device.deletedAt ? 'deleted' : ''}`}
                  onClick={() => canReadDevices && setSelectedDevice(device)}
                >
                  <td className="detail-text">{formatDeviceModel(device)}</td>
                  <td className="serial-text">{device.serialNumber || '-'}</td>
                  <td className="detail-text">{formatOwner(device.firstOwner, device.firstOwnerUuid)}</td>
                  <td className="detail-text">{formatOwner(device.owner, device.ownerUuid)}</td>
                  <td className="detail-text">{formatDate(device.createdAt)}</td>
                  <td className="detail-text">{formatDate(device.updatedAt)}</td>
                  <td className="detail-text">{device.version || '-'}</td>
                  <td>{device.upgradable
                    ? <span className="status-badge upgradable">{t("가능")}</span>
                    : <span className="status-badge not-upgradable">{t("최신버전")}</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button className="action-btn" onClick={(e) => {
                      e.stopPropagation();
                      if (!canReadDevices) return;
                      setSelectedDevice(device);
                    }} disabled={!canReadDevices}>
                      {t("상세 보기")}
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
                  "{searchTerm}" 에 대한 검색 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          currentPage={currentPage}
          total={total}
          pageSize={PAGE_SIZE}
          info={getPaginationInfo()}
          disabled={loading}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Drawer Overlay */}
      {selectedDevice && (
        <div className="drawer-overlay" onClick={() => setSelectedDevice(null)}></div>
      )}

      {/* Drawer Panel */}
      <div className={`detail-drawer ${selectedDevice ? 'open' : ''}`}>
        {selectedDevice && (
          <>
            <div className="drawer-header">
              <h3>{t("기기 상세 정보")}</h3>
              <button className="drawer-close-btn" onClick={() => setSelectedDevice(null)}>
                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <div className="drawer-content">
              {/* Profile Summary */}
              <div className="drawer-profile-card">
                <div className="drawer-profile-info">
                  <div className="drawer-model">
                    <span className="drawer-label">{t("시리얼 번호")}</span>
                  </div>
                  <div className="drawer-name">
                    {selectedDevice.serialNumber}
                  </div>
                  <div className="drawer-model" style={{ marginTop: '10px' }}>
                    <span className="drawer-label">{t("모델 · 제조번호")}</span>
                    {formatDeviceModel(selectedDevice)} · {selectedDevice.manufactureNumber || '-'}
                  </div>
                </div>
              </div>

              {/* 기기 기본 정보 */}
              <div className="drawer-section">
                <h4>{t("기기 기본 정보")}</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>{t("모델")}</label>
                    <span>{formatDeviceModel(selectedDevice)}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("시리얼 번호")}</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{selectedDevice.serialNumber || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("제조 번호")}</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{selectedDevice.manufactureNumber || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("설명")}</label>
                    <span>{selectedDevice.description || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("등록일 (CreatedAt)")}</label>
                    <span>{formatDate(selectedDevice.createdAt, true)}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("최종 수정일")}</label>
                    <span>{formatDate(selectedDevice.updatedAt, true)}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("삭제 여부")}</label>
                    <span>{selectedDevice.deletedAt ? formatDate(selectedDevice.deletedAt, true) : '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("고유 식별자 (UUID)")}</label>
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>{selectedDevice.uuid}</span>
                  </div>
                </div>
              </div>

              {/* 소유 이력 */}
              <div className="drawer-section">
                <h4>{t("소유 이력")}</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>{t("최초 소유자")}</label>
                    <span>{formatOwner(selectedDevice.firstOwner, selectedDevice.firstOwnerUuid)}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("현재 소유자")}</label>
                    <span>{formatOwner(selectedDevice.owner, selectedDevice.ownerUuid)}</span>
                  </div>
                  {selectedDevice.firstOwnerUuid && selectedDevice.ownerUuid && selectedDevice.firstOwnerUuid !== selectedDevice.ownerUuid && (
                    <div className="info-item">
                      <label>{t("양도 여부")}</label>
                      <span style={{ color: '#d97706', fontWeight: 600 }}>{t("양도됨")}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <label>Wallet UUIDs</label>
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>{formatWalletUuids(selectedDevice.walletUuids)}</span>
                  </div>
                </div>
              </div>

              {/* 펌웨어 정보 */}
              <div className="drawer-section">
                <h4>{t("펌웨어 정보")}</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>{t("버전 (Version)")}</label>
                    <span>{selectedDevice.version || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("업그레이드 가능 여부")}</label>
                    <span>{selectedDevice.upgradable
                      ? <span className="status-badge upgradable">{t("가능")}</span>
                      : <span className="status-badge not-upgradable">{t("최신버전")}</span>}
                    </span>
                  </div>
                </div>
              </div>

              {/* 네트워크 정보 */}
              <div className="drawer-section">
                <h4>{t("네트워크 정보")}</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>MAC Address</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{selectedDevice.macAddress || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Bluetooth Address</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{selectedDevice.btAddress || '-'}</span>
                  </div>
                </div>
              </div>

            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DevicesPage;
